/**
 * PROJECT-OVERVIEW §7.5 — Administração.
 *
 * 🔒 **These are the API Adm aggregates.** A module screen never fetches them —
 * `eslint.config.js` blocks the import, and that lint rule is the coarse half of
 * the authorization story (§5.5).
 *
 * 🔒 **BR-18 is the whole point of this file's shape.** `Grupo` carries **no
 * client reference**: it is global and expresses functional permission only. A
 * user separately holds a list of `grupos` and a list of `clientes`, and the two
 * are INDEPENDENT. Effective permission is the **union** of the user's grupos,
 * applied to whichever client is selected. There is no runtime profile picker —
 * never build one.
 *
 * 🔒 **Authorization never comes from token claims.** These lists arrive here, as
 * application data from API User / API Adm. The Cognito adapter deliberately
 * produces `grupos: []` and `clientes: []` even when the ID token carries
 * `cognito:groups`, and a test guards it.
 */
import { z } from 'zod'

import { id, valorDeCatalogo } from './comum'

export const clienteSchema = z.object({
  idCliente: id,
  nome: z.string(),
  /** Which modules this client sees at all. */
  modulosHabilitados: z.array(z.string()),
  /**
   * BR-11 — every feature/component must be flaggable.
   *
   * ⚠️ **`.default({})` porque este é o primeiro JSONB servido em produção.**
   * `TEXT[]` está provado — `exigeCampos` já atravessa o mesmo caminho com o
   * FinOps em Postgres — mas JSONB não tem precedente algum: conforme o driver,
   * ou numa eventual troca para a RDS Data API, a coluna pode chegar como string
   * em vez de objeto.
   *
   * E o custo do erro é desproporcional: `/eu` é a rota que TODA conta chama no
   * boot, e uma falha de contrato aqui não degrada uma tela — derruba a
   * aplicação inteira em `SemPerfil`, sem botão de "tentar de novo" (ver
   * `perfil.ts`). Um campo que só decide se o assistente de IA aparece (é o que
   * `flags/flagsDoCliente` lê dele desde 05/10/2026) não pode ter esse poder.
   *
   * O backend também normaliza, dos dois lados. Isto é o cinto além do
   * suspensório, e é barato.
   */
  featureFlags: z.record(z.string(), z.boolean()).default({}),
})

export type Cliente = z.infer<typeof clienteSchema>

/**
 * BR-03 / V0013 — the day of the month the client's billing cycle starts
 * (`administracao.clientes.dia_fiscal_inicio`, `CHECK BETWEEN 1 AND 31`, DEFAULT 1 =
 * calendar month).
 */
const diaFiscalInicio = z.number().int().min(1).max(31)

/**
 * `POST /clientes` (API Adm) — the `2n` "novo cliente" form.
 *
 * 🔒 **Only columns that already exist in `administracao.clientes`.** Fábio decided
 * this on 2026-09-17 while the form's fields are still open with iOX (handoff §7
 * item 7). `idCliente` is NOT sent: the Lambda derives the slug from `nome`, the
 * same way `POST /grupos` does. `ativo` and `featureFlags` are not sent either —
 * they are born with the table DEFAULTs (TRUE and `{}`).
 *
 * ⚠️ `diaFiscalInicio` is not sent since 2026-10-15 (Fábio removed it from the
 * form). The Lambda still accepts it but falls back to 1 when it is absent, and the
 * contract's day in Orçamento is the one that counts anyway (V0013).
 */
export const novoClienteSchema = z.object({
  nome: z.string().trim().min(1),
  /** Subset of `finops | orcamento | compliance` — the Lambda rejects anything else. */
  modulosHabilitados: z.array(z.string()),
})

export type NovoCliente = z.infer<typeof novoClienteSchema>

/**
 * `201` body of `POST /clientes`: the list shape plus the fiscal day that was saved.
 *
 * ⚠️ `diaFiscalInicio` exists ONLY here. `GET /clientes` does not return it (the
 * Postgres repository never selected the column), so it is not added to
 * `clienteSchema` — that would make every listing fail the contract.
 */
export const clienteCriadoSchema = clienteSchema.extend({ diaFiscalInicio })

export type ClienteCriado = z.infer<typeof clienteCriadoSchema>

export const usuarioSchema = z.object({
  /**
   * Cognito carries identity, and identity only.
   *
   * ⚠️ **Nullable, and that is the domain talking.** The `sub` only exists after
   * the account is created in Cognito, but an Administrador registers people who
   * have not signed in yet — which is precisely what screen `2o` is for. The
   * backend's `V0002__administracao.sql` says the same thing about the column,
   * and `seed_administracao.json` ships all three users without a `sub`.
   *
   * It was `id` (min 1) until 2026-08-29, so `GET /usuarios` threw at the zod
   * boundary and `2o` failed to render at all against the real gateway.
   *
   * 🔑 **`email` is the functional key**, not this. The Adm Lambda already
   * accepts either one in `PUT|DELETE /usuarios/{id}`.
   */
  cognitoSub: z.string().nullable().optional(),
  nome: z.string(),
  email: z.string(),
  /** Functional, **global** — carries no client reference (BR-18). */
  grupos: z.array(z.string()),
  /** Access scope — **INDEPENDENT** of `grupos` (BR-18). */
  clientes: z.array(z.string()),

  /**
   * Conta ativa.
   *
   * ⚠️ Excluir usuário é **desativar** — decisão do Fábio em 01/09/2026, para
   * preservar o rastro de auditoria. E não seria escolha de qualquer forma:
   * `finops.oportunidades.criado_por` e outras cinco FKs são NO ACTION, então
   * apagar quem já criou alguma coisa é impossível no banco. A V0015 nem concede
   * DELETE em `administracao.usuarios`.
   *
   * `.default(true)` porque o repositório `seed` não declara o campo — quem
   * nunca foi desativado é ativo, que é o DEFAULT TRUE da coluna.
   */
  ativo: z.boolean().default(true),
})

export type Usuario = z.infer<typeof usuarioSchema>

export const conviteSchema = z.object({
  /**
   * ⚠️ Nullable since 2026-10-26: an entry can describe a Cognito account that
   * has **no invitation row at all**.
   *
   * `GET /convites` is driven by rows of `administracao.convites`, so anyone
   * created straight in the pool by CLI (`admin-create-user` — how the three iOX
   * accounts were made) was simply absent from the response, and the fallback
   * below painted them green forever. On 2026-10-24 that is how Arimatéa's
   * **expired** invitation rendered as "Ativo", which is why the resend had to
   * be done by CLI. The route now also emits those accounts, with `idConvite`
   * `null`: there is no invitation, there is an account. Inventing an id would
   * claim the platform sent an e-mail it never sent.
   */
  idConvite: id.nullable(),
  email: z.string(),
  /**
   * Invitation only (BR-08/09) — there is no self sign-up path that produces this.
   *
   * ⚠️ Open catalog, not `z.enum`. The database allows `expirado` and `revogado`
   * on top of these three (`V0002__administracao.sql`), and both are reachable:
   * the invitation's temporary password expires after 7 days, and
   * `FORCE_CHANGE_PASSWORD` blocks the self-service reset. A revoked invitation
   * used to hard-fail the whole of screen `2o` at the zod boundary.
   */
  status: valorDeCatalogo,
  /**
   * Whether `status` reflects the **live** Cognito pool, or is the stale cached
   * column served because the pool could not be reached.
   *
   * ⚠️ This field exists because the silent fallback hid a real defect for six
   * weeks. From 2026-09-09 to 2026-10-24 the Lambda called `admin_list_users` —
   * a method boto3 does not have — so every request threw, was swallowed by the
   * route's `except`, and answered 200 with the cached column. Nothing on screen
   * said so, and a `CONFIRMED` account rendered as `expirado`. Degrading still
   * beats a 500 (screen `2o` also carries clientes, grupos and vínculos), but
   * the column has to be able to say *indisponível* rather than present cache as
   * truth.
   *
   * `.default(true)` keeps older responses valid — a backend that does not send
   * the field is one whose status came from wherever it always came from.
   */
  statusVivo: z.boolean().default(true),
})

export type Convite = z.infer<typeof conviteSchema>

/**
 * ✅ Two levers per row, not one — confirmed by Eduardo on 2026-08-13 (§5.3).
 *
 * Shot 2q draws a single `Exibir` checkbox and is **out of date**: the visibility
 * tree carries `Exibir` AND `Editar`. `nivel` is the backend's expression of the
 * same thing (`PERSONAS-ref.md` §6); the resolver in story 0.4 turns it into
 * `Map<tela, {ver, editar}>`.
 */
export const permissaoSchema = z.object({
  idFuncionalidade: id,
  nivel: z.enum(['leitura', 'escrita', 'nenhum']),
})

export type Permissao = z.infer<typeof permissaoSchema>

export const grupoSchema = z.object({
  idGrupo: id,
  nome: z.string(),
  permissoes: z.array(permissaoSchema),
  // 🔒 NO client reference here. That absence IS BR-18 — see the file header.

  /**
   * Um dos grupos que a plataforma já traz (`padrão` na `2q`) ou um criado por um
   * Administrador em runtime (`criado pelo admin`).
   *
   * ⚠️ **É um CAMPO porque a alternativa era adivinhar pelo id, e adivinhar estava
   * errado.** A `2q` rotulava a linha testando `idGrupo.startsWith('gr-')` — um prefixo
   * que só existia nas fixtures do frontend. O seed do backend nomeia os mesmos grupos
   * `administrador`/`arquiteto-iox`/`cliente`, então no dia em que o endereço real
   * respondesse, TODO grupo apareceria como "criado pelo admin".
   *
   * `.default(false)` e não obrigatório: um grupo criado por uma versão do backend que
   * ainda não mande o campo é, por definição, um grupo criado pelo admin.
   */
  padrao: z.boolean().default(false),
})

export type Grupo = z.infer<typeof grupoSchema>

/**
 * The one grupo `DELETE /grupos/{id}` refuses (409 `GRUPO_PROTEGIDO`, decided by Fábio
 * on 2026-09-17): without it nobody could grant `admin.perfis` back through the UI.
 * The id is the backend seed's / V0012's. The screen hides the action for it; the
 * Lambda is what enforces the rule.
 */
export const ID_GRUPO_ADMINISTRADOR = 'administrador'
